import React, { useEffect, useRef, useState } from "react";
import Page from "./Page";
import { X_PADDING } from "../constants";

const TOP_PROGRAMS = [
  {
    title: "Master of Science in Infectious diseases ",
    image: "/images/img_15.jpg",
    color: "from-[#1477c2] to-[#1b9de3]",
    description: "Specialized expertise in managing infectious diseases",
  },
  {
    title: "Master of Public Health",
    image: "/images/img_46.jpg",
    color: "from-[#16365c] to-[#1477c2]",
    description: "Population health and policy leadership",
  },
  {
    title: "MSc Clinical Ophthalmology",
    image: "/images/img_55.jpg",
    color: "from-[#1b9de3] to-[#0d5a8a]",
    description: "Eye care and vision health specialization",
  },
];

const CLINICAL_MEDICINE_BRANCHES = [
  {
    name: "Tropical Medicine",
    image: "/images/img_36.jpg",
    description:
      "Study and management of tropical diseases in resource-limited settings",
  },
  {
    name: "Anesthesia and critical care",
    image: "/images/img_37.jpg",
    description:
      "Advanced patient care during surgery and critical illness management",
  },
  {
    name: "Dermatovenereology",
    image: "/images/img_40.jpg",
    description:
      "Diagnosis and treatment of skin diseases and sexually transmitted infections",
  },
  {
    name: "Community psychiatry and mental health",
    image: "/images/img_21.jpg",
    description:
      "Mental health care delivery and psychiatric services in community settings",
  },
  {
    name: "ENT",
    image: "/images/img_49.jpg",
    description: "Diagnosis and treatment of ear, nose, and throat disorders",
  },
  {
    name: "Cancer and Clinical Oncology",
    image: "/images/img_41.jpg",
    description: "Cancer diagnosis, treatment, and palliative care management",
  },
];

const SpecializedClinicians = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const sectionRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleMouseEnter = (id) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredId(id);
    }, 100);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredId(null);
  };

  const getCardId = (type, index) => `${type}-${index}`;

  return (
    <Page name="Specialized Clinicians ">
      <div className={`w-full bg-white pt-8 pb-16 ${X_PADDING}`}>
        <div className="w-full mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#16365c] mb-4">
              Specializations
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              COAZ supports the advancement of specialized clinical practice and
              is actively advocating for the introduction and recognition of
              Master's programmes in key clinical specialties to strengthen
              healthcare delivery and expand professional career pathways.
            </p>
            <div className="w-16 h-1 bg-[#1477c2] mx-auto mt-4 rounded-full"></div>
          </div>

          {/* Top Cards - Colored with images */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            {TOP_PROGRAMS.map(({ title, image, color, description }, index) => {
              const cardId = getCardId("top", index);
              const isExpanded = hoveredId === cardId;
              const isShrunk = hoveredId !== null && hoveredId !== cardId;

              return (
                <div
                  key={cardId}
                  className={`
                    transform transition-all duration-700 ease-out
                    ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}
                  `}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div
                    className={`
                      rounded-xl shadow-md hover:shadow-xl transition-all duration-500 ease-in-out
                      overflow-hidden group relative
                      ${isExpanded ? "scale-110 shadow-2xl z-20" : ""}
                      ${isShrunk ? "scale-95 opacity-50" : ""}
                    `}
                    onMouseEnter={() => handleMouseEnter(cardId)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {/* Animated Border - Top Left Corner Drawing Effect */}
                    <div
                      className={`
                      absolute inset-0 rounded-xl pointer-events-none
                      transition-all duration-700 ease-in-out
                      ${isExpanded ? "border-[4px] border-[#1477c2]" : "border-[4px] border-transparent"}
                    `}
                    >
                      {/* Top line - left to right */}
                      <div
                        className={`
                        absolute top-0 left-0 w-0 h-0.5 bg-[#1477c2] transition-all duration-700 ease-in-out
                        ${isExpanded ? "w-full" : "w-0"}
                      `}
                      ></div>
                      {/* Left line - top to bottom */}
                      <div
                        className={`
                        absolute top-0 left-0 w-0.5 h-0 bg-[#1477c2] transition-all duration-700 ease-in-out delay-100
                        ${isExpanded ? "h-full" : "h-0"}
                      `}
                      ></div>
                      {/* Bottom line - right to left */}
                      <div
                        className={`
                        absolute bottom-0 right-0 w-0 h-0.5 bg-[#1477c2] transition-all duration-700 ease-in-out delay-200
                        ${isExpanded ? "w-full" : "w-0"}
                      `}
                      ></div>
                      {/* Right line - bottom to top */}
                      <div
                        className={`
                        absolute bottom-0 right-0 w-0.5 h-0 bg-[#1477c2] transition-all duration-700 ease-in-out delay-300
                        ${isExpanded ? "h-full" : "h-0"}
                      `}
                      ></div>
                    </div>

                    <div
                      className={`h-28 flex flex-col items-center justify-center bg-gradient-to-br ${color} relative overflow-hidden`}
                    >
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
                      <img
                        src={image}
                        alt={title}
                        className={`h-14 w-14 object-cover rounded-full border-2 border-white/30 relative z-10 transition-all duration-500 ${isExpanded ? "h-20 w-20" : "group-hover:scale-110"}`}
                      />
                      <div className="absolute -right-8 -top-8 w-24 h-24 bg-white/10 rounded-full"></div>
                      <div className="absolute -left-8 -bottom-8 w-20 h-20 bg-white/5 rounded-full"></div>
                    </div>
                    <div className="p-4 bg-white border-x border-b border-[#eef1f4] rounded-b-xl">
                      <p
                        className={`font-bold text-sm text-[#16365c] text-center transition-all duration-500 ${isExpanded ? "text-[#1477c2] text-base" : ""}`}
                      >
                        {title}
                      </p>
                      <p
                        className={`text-xs text-gray-400 text-center mt-1 transition-all duration-500 ${isExpanded ? "text-[#16365c]" : ""}`}
                      >
                        {description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Clinical Medicine Section */}
          <div ref={sectionRef}>
            <div className="rounded-xl shadow-md border border-[#eef1f4] overflow-hidden">
              <div className="p-6 bg-[#f8fafb]">
                <div className="text-lg font-bold text-[#16365c] mb-4">
                  MSc Clinical Medicine
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {CLINICAL_MEDICINE_BRANCHES.map((branch, index) => {
                    const cardId = getCardId("branch", index);
                    const isExpanded = hoveredId === cardId;
                    const isShrunk = hoveredId !== null && hoveredId !== cardId;

                    return (
                      <div
                        key={cardId}
                        className={`
                          transform transition-all duration-700 ease-out
                          ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}
                        `}
                        style={{ transitionDelay: `${index * 100}ms` }}
                      >
                        <div
                          className={`
                            bg-white rounded-lg border border-[#eef1f4] p-4 shadow-sm 
                            transition-all duration-500 ease-in-out
                            relative
                            ${isExpanded ? "scale-110 shadow-2xl z-20" : ""}
                            ${isShrunk ? "scale-95 opacity-50" : ""}
                          `}
                          onMouseEnter={() => handleMouseEnter(cardId)}
                          onMouseLeave={handleMouseLeave}
                        >
                          {/* Animated Border - Top Left Corner Drawing Effect */}
                          <div
                            className={`
                            absolute inset-0 rounded-lg pointer-events-none
                            transition-all duration-700 ease-in-out
                            ${isExpanded ? "border-[4px] border-[#1477c2]" : "border-[4px] border-transparent"}
                          `}
                          >
                            {/* Top line - left to right */}
                            <div
                              className={`
                              absolute top-0 left-0 w-0 h-0.5 bg-[#1477c2] transition-all duration-700 ease-in-out
                              ${isExpanded ? "w-full" : "w-0"}
                            `}
                            ></div>
                            {/* Left line - top to bottom */}
                            <div
                              className={`
                              absolute top-0 left-0 w-0.5 h-0 bg-[#1477c2] transition-all duration-700 ease-in-out delay-100
                              ${isExpanded ? "h-full" : "h-0"}
                            `}
                            ></div>
                            {/* Bottom line - right to left */}
                            <div
                              className={`
                              absolute bottom-0 right-0 w-0 h-0.5 bg-[#1477c2] transition-all duration-700 ease-in-out delay-200
                              ${isExpanded ? "w-full" : "w-0"}
                            `}
                            ></div>
                            {/* Right line - bottom to top */}
                            <div
                              className={`
                              absolute bottom-0 right-0 w-0.5 h-0 bg-[#1477c2] transition-all duration-700 ease-in-out delay-300
                              ${isExpanded ? "h-full" : "h-0"}
                            `}
                            ></div>
                          </div>

                          <div className="flex items-start gap-3 relative z-10">
                            <div
                              className={`w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 border border-[#eef1f4] transition-all duration-500 ${isExpanded ? "w-20 h-20" : ""}`}
                            >
                              <img
                                src={branch.image}
                                alt={branch.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-sm font-semibold text-[#16365c] transition-all duration-500 ${isExpanded ? "text-[#1477c2] text-base" : ""}`}
                              >
                                {branch.name}
                              </p>
                              <p
                                className={`text-xs text-[#7c8a97] mt-1 leading-relaxed transition-all duration-500 ${isExpanded ? "text-[#16365c]" : ""}`}
                              >
                                {branch.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default SpecializedClinicians;
